/* eslint-disable react/require-default-props */
import React, {useEffect, useMemo, useState} from "react";
import {
    Button,
    CloseIconButton,
    CustomSlider,
    GoogleMapsSearch,
    GPlacesAutocomplete2,
    Input,
    Modal,
    SvgIcons,
} from "@tripian/react";
import Model, {helper} from "@tripian/model";
import moment from "moment";
import useStep from "../../hooks/useStep";
import useTrip from "../../hooks/useTrip";
import useTranslate from "../../hooks/useTranslate";
import ReactDOM from "react-dom";
import classes from "./CustomPoiModal.module.scss";

type CustomStep = {
  customPoi: Model.CustomPoi;
  stepType: string;
  startTime: string;
  endTime: string;
};

interface IStoragePoiModal {
  dayIndex: number;
  plansIdDate: {
    id: number;
    date: string;
  }[];
  showModal: boolean;
  setShowModal: () => void;
  storageName: string;
  storageCoordinate: Model.Coordinate;
  storageAddress: string;
}

const StoragePoiModal: React.FC<IStoragePoiModal> = ({
  dayIndex = 0,
  plansIdDate,
  showModal,
  setShowModal,
  storageName,
  storageCoordinate,
  storageAddress,
}) => {
  const [error, setError] = useState<string | undefined>(undefined);

  const initialData: CustomStep = {
    customPoi: {
      name: storageName,
      coordinate: storageCoordinate || { lat: 1, lng: 1 },
      address: storageAddress,
      description: undefined,
    },
    stepType: "luggage",
    startTime: moment(new Date()).set({ hour: 9, minute: 0 }).format("HH:mm"),
    endTime: moment(new Date()).set({ hour: 9, minute: 30 }).format("HH:mm"),
  };

  const [data, setData] = useState<CustomStep>(initialData);
  const [address, setAddress] = useState<string | undefined>(storageAddress);
  const [coordinate, setCoordinate] =
    useState<Model.Coordinate>(storageCoordinate);
  const [selectedPlanId, setSelectedPlanId] = useState<number>(
    plansIdDate[dayIndex].id
  );

  const { customStepAdd } = useStep();
  const { tripReference } = useTrip();
  const { t } = useTranslate();

  const geocoder = useMemo(() => new google.maps.Geocoder(), []);

  useEffect(() => {
    setSelectedPlanId(plansIdDate[dayIndex]?.id);
  }, [dayIndex, plansIdDate]);

  // Handle ESC key to close modal
  useEffect(() => {
    const handleEscapeKey = (event: KeyboardEvent) => {
      if (event.key === "Escape" && showModal) {
        handleClose();
      }
    };

    if (showModal) {
      document.addEventListener("keydown", handleEscapeKey);
    }

    return () => {
      document.removeEventListener("keydown", handleEscapeKey);
    };
  }, [showModal]);

  useEffect(() => {
    if (showModal) {
      const newData: CustomStep = {
        customPoi: {
          name: storageName,
          coordinate: storageCoordinate,
          address: storageAddress,
          description: undefined,
        },
        stepType: "luggage",
        startTime: moment(new Date())
          .set({ hour: 9, minute: 0 })
          .format("HH:mm"),
        endTime: moment(new Date())
          .set({ hour: 9, minute: 30 })
          .format("HH:mm"),
      };
      setData(newData);
      setAddress(storageAddress);
      setCoordinate(storageCoordinate);

      if (storageCoordinate.lat && storageCoordinate.lng) {
        geocoder.geocode(
          {
            location: {
              lat: storageCoordinate.lat,
              lng: storageCoordinate.lng,
            },
          },
          (results, status) => {
            if (status === google.maps.GeocoderStatus.OK) {
              if (results !== null && results.length > 0) {
                const formattedAddress = results[0]?.formatted_address;
                console.log("Storage geocoded address:", formattedAddress);
                setAddress(formattedAddress);
                const updatedData = { ...newData };
                updatedData.customPoi.address = formattedAddress;
                setData(updatedData);
              }
            } else {
              console.error("Storage geocode error:", status);
            }
          }
        );
      }
    }
  }, [showModal, storageName, storageCoordinate, storageAddress, geocoder]);

  const startTimeOptions = useMemo(() => {
      return helper.startTimeRange().map((time: string) => ({
        key: undefined,
        value: time,
    }));
  }, []);

  const endTimeOptions = useMemo(() => {
    return helper.endTimeRange(data.startTime).map((time: string) => ({
      key: undefined,
      value: time,
    }));
  }, [data?.startTime]);

  const callbackStartTime = (startTime: string | number) => {
    const newData: CustomStep = helper.deepCopy(data);

    const start = moment()
      .set({
        hour: +startTime.toString().slice(0, 2),
        minute: +startTime.toString().slice(3, 5),
      })
      .format("HH:mm");

    const end = moment(start, "HH:mm").add(30, "minutes").format("HH:mm");

    newData.startTime = start;
    newData.endTime = end;
    setData(newData);
  };

  const callbackEndTime = (endTime: string | number) => {
    const newData: CustomStep = helper.deepCopy(data);

    newData.endTime = moment()
        .set({
            hour: +endTime.toString().slice(0, 2),
            minute: +endTime.toString().slice(3, 5),
        })
        .format("HH:mm");
    setData(newData);
  };

  const handleChangeName = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newData: CustomStep = helper.deepCopy(data);
    newData.customPoi.name = event.target.value;
    setData(newData);
  };

  const handleChangeDescription = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const newData: CustomStep = helper.deepCopy(data);
    newData.customPoi.description = event.target.value;
    setData(newData);
  };

  const handleImageUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      // Validate file type
      const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
      if (!validTypes.includes(file.type)) {
        setError(t("trips.myTrips.itinerary.customPoiModal.image.invalidFileType"));
        return;
      }
      // Validate file size (max 5MB)
      const maxSize = 5 * 1024 * 1024;
      if (file.size > maxSize) {
        setError(t("trips.myTrips.itinerary.customPoiModal.image.fileTooLarge"));
        return;
      }

      // Convert to base64
      const reader = new FileReader();
      reader.onload = () => {
        const base64String = reader.result as string;
        const newData: CustomStep = helper.deepCopy(data);
        newData.customPoi.image = base64String;
        setData(newData);
        setError(undefined);
      };
      reader.onerror = () => {
        setError("Failed to read image file");
      };
      reader.readAsDataURL(file);
    }
  };

  const handleRemoveImage = () => {
    const newData: CustomStep = helper.deepCopy(data);
    newData.customPoi.image = undefined;
    setData(newData);
  };

  const callbackGPlacesAutocomplete = (
    googlePlace: google.maps.places.PlaceResult
  ) => {
    if (!googlePlace.formatted_address) {
      setAddress(undefined);
      return;
    }

    setAddress(googlePlace.formatted_address);
  };

  const googleMapOnMapClick = (coordinate: Model.Coordinate) => {
    setCoordinate(coordinate);
    geocoder.geocode({ location: coordinate }, (results, status) => {
      if (status === google.maps.GeocoderStatus.OK) {
        if (results !== null && results.length > 0) {
          const formattedAddress = results[0]?.formatted_address;
          console.log("Geocoded address:", formattedAddress);
          setAddress(formattedAddress);
        }
      } else {
        console.error("Geocode error:", status);
      }
    });
  };

  const disabled = useMemo(() => {
    return !(
      data.customPoi.name &&
      address &&
      coordinate &&
      data.startTime &&
      data.endTime
    );
  }, [data.customPoi.name, address, coordinate, data.startTime, data.endTime]);

  const resetStates = () => {
    setError(undefined);
    const resetData: CustomStep = {
      customPoi: {
        name: storageName,
        coordinate: storageCoordinate,
        address: storageAddress,
        description: undefined,
      },
      stepType: "luggage",
      startTime: moment(new Date()).set({ hour: 9, minute: 0 }).format("HH:mm"),
      endTime: moment(new Date()).set({ hour: 9, minute: 30 }).format("HH:mm"),
    };
    setData(resetData);
    setAddress(storageAddress);
    setCoordinate(storageCoordinate);
    setSelectedPlanId(plansIdDate[dayIndex]?.id);
  };

  const handleClose = () => {
    resetStates();
    setShowModal();
  };

  const handleSubmit = () => {
    setError(undefined);

    const customPoi: Model.CustomPoi = {
      ...data.customPoi,
      address: address || "",
      coordinate: coordinate || { lat: 1, lng: 1 },
    };

    customStepAdd(
      selectedPlanId,
      customPoi,
      data.stepType,
      data.startTime,
      data.endTime
    )
      .then(() => {
        handleClose();
      })
      .catch(err => {
        setError(err);
      });
  };

  return ReactDOM.createPortal(
    <Modal
      className="!h-[90%] !min-w-[90%] !w-[90%] bg-background-color md:!min-w-[40%] md:!w-40 !z-[1501] rounded-[2.5rem] !overflow-hidden"
      show={showModal}
      zIndex={1500}
    >
      <div className="flex flex-col h-full overflow-hidden">
        {/* Sticky Header */}
        <div className="flex items-center justify-between w-full px-8 pt-8 pb-4 flex-shrink-0 border-b border-gray-200">
          <div className="text-2xl font-semibold">
            {t("trips.myTrips.itinerary.customPoiModal.addACustomEvent")}
          </div>
          <CloseIconButton
            className="w-6 h-6"
            fill="#2B2B33"
            clicked={handleClose}
          />
        </div>

        {/* Scrollable Content */}
        <div
          className={`flex-1 overflow-y-auto px-2 pt-4 ${classes.modalContent} ${classes.hideScroll}`}
        >
          {/* 1. Select a Day */}
          <div className="flex justify-start flex-col w-full md:mb-6 mb-4 px-6">
            <div className="font-semibold mb-2">
              1. {t("trips.myTrips.itinerary.customPoiModal.selectADay")}*
            </div>
            {plansIdDate.length > 0 ? (
              <div className={classes.sliderContainer}>
                <CustomSlider scrollSize={150}>
                  {plansIdDate.map((planIdDate, index) => {
                    return (
                      <div
                        key={index}
                        className={`text-sm !w-auto rounded-3xl border border-solid border-gray-300 my-0 py-[0.4375rem] px-4 cursor-pointer min-w-fit font-medium whitespace-nowrap ${
                          selectedPlanId === planIdDate.id
                            ? "text-white bg-primary-color"
                            : "text-black bg-white"
                        }`}
                        onClick={() => setSelectedPlanId(planIdDate.id)}
                      >
                        <span
                          className={`${selectedPlanId === planIdDate.id ? "font-bold" : "font-medium"}`}
                        >
                          {t("trips.myTrips.itinerary.day")}{" "}
                          <span>{index + 1}</span>
                        </span>
                        {selectedPlanId === planIdDate.id && (
                          <span>
                            <span> &nbsp;|&nbsp; </span>
                            <span>
                              {moment(planIdDate.date).format("MMM DD")}
                            </span>
                          </span>
                        )}
                      </div>
                    );
                  })}
                </CustomSlider>
              </div>
            ) : null}
          </div>

          {/* 2. Poi/Event Name */}
          <div className="flex justify-start flex-col w-full md:mb-6 mb-4 px-6">
            <div className="font-semibold mb-2">
              2. {t("trips.myTrips.itinerary.customPoiModal.stepDetails.event")}
              &nbsp;{t("trips.myTrips.itinerary.customPoiModal.name.title")}*
            </div>
            <div>
              <Input
                type="text"
                placeholder={t(
                  "trips.myTrips.itinerary.customPoiModal.name.placeholder"
                )}
                name="custompoi"
                value={data.customPoi.name}
                onChange={handleChangeName}
              />
            </div>
          </div>

          {/* 3. Address */}
          <div className="flex justify-start flex-col w-full md:mb-6 mb-4 px-6">
            <div className="font-semibold mb-2">
              3.{" "}
              {t(
                "trips.myTrips.itinerary.customPoiModal.locationModal.addLocation.address.title"
              )}
              *
            </div>
            <div>
              <GPlacesAutocomplete2
                placeholder={t(
                  "trips.myTrips.itinerary.customPoiModal.locationModal.addLocation.address.placeholder"
                )}
                initialText={address}
                onSelectedChanged={callbackGPlacesAutocomplete}
              />
            </div>
            {tripReference && (
              <div className="flex justify-start flex-col w-full relative h-[300px] mt-4">
                <GoogleMapsSearch
                  zoom={16}
                  center={storageCoordinate}
                  coordinate={coordinate}
                  onChange={googleMapOnMapClick}
                />
              </div>
            )}
          </div>

          {/* 4. Select a Time */}
          <div className="flex justify-start self-start flex-col md:mb-6 mb-4 px-6">
            <div className="font-semibold mb-3">
              4. {t("trips.myTrips.itinerary.customPoiModal.visitTime.title")}*
            </div>
            <div className="flex items-center justify-between border border-gray-300 rounded-full px-8 py-[11.45px] hover:border-black/75 focus-within:border-black/75">
              <select
                className="appearance-none bg-none border-none px-0 py-0 m-0 w-[100px] text-center text-gray-800 text-base cursor-pointer focus:outline-none"
                value={data.startTime}
                onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
                  callbackStartTime(e.target.value)
                }
              >
                {startTimeOptions.map(opt => (
                  <option key={opt.value} value={opt.value}>
                    {opt.value}
                  </option>
                ))}
              </select>

              <span className="text-xl text-gray-800 pointer-events-none">
                <SvgIcons.ChevronRight stroke="#000" />
              </span>

              <select
                className="appearance-none bg-none border-none px-0 py-0 m-0 w-[100px] text-center text-gray-800 text-base cursor-pointer focus:outline-none"
                value={data.endTime}
                onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
                  callbackEndTime(e.target.value)
                }
              >
                {endTimeOptions.map(opt => (
                  <option key={opt.value} value={opt.value}>
                    {opt.value}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* 5. Description */}
          <div className="flex justify-start flex-col w-full md:mb-6 mb-4 px-6">
            <div className="font-semibold mb-2">
              5. {t("trips.myTrips.itinerary.customPoiModal.description.title")}
            </div>
            <div>
              <Input
                type="text"
                placeholder={t(
                  "trips.myTrips.itinerary.customPoiModal.description.placeholder"
                )}
                name="description"
                value={data.customPoi.description || ""}
                onChange={handleChangeDescription}
              />
            </div>
          </div>

          {/* 6. Image Upload */}
          <div className="flex justify-start flex-col w-full md:mb-6 mb-4 px-6">
            <div className="font-semibold mb-2">
              6. {t("trips.myTrips.itinerary.customPoiModal.image.title")}
            </div>
            <div className="flex flex-col gap-3">
              {data.customPoi.image ? (
                <div className="relative w-full">
                  <img
                    src={data.customPoi.image}
                    alt="Preview"
                    className="w-full h-48 object-cover rounded-lg"
                  />
                  <button
                    type="button"
                    onClick={handleRemoveImage}
                    className="absolute top-2 right-2 bg-white rounded-full p-2 shadow-md hover:bg-gray-100"
                  >
                    <svg className="w-4 h-4" fill="#000" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                      <path d="M18.3 5.71a.996.996 0 0 0-1.41 0L12 10.59 7.11 5.7A.996.996 0 1 0 5.7 7.11L10.59 12 5.7 16.89a.996.996 0 1 0 1.41 1.41L12 13.41l4.89 4.89a.996.996 0 1 0 1.41-1.41L13.41 12l4.89-4.89c.38-.38.38-1.02 0-1.4z" />
                    </svg>
                  </button>
                </div>
              ) : (
                <label className="flex flex-col items-center justify-center w-full h-32 border-2 border-gray-300 border-dashed rounded-lg cursor-pointer hover:border-gray-400 hover:bg-gray-50">
                  <div className="flex flex-col items-center justify-center pt-5 pb-6">
                    <svg className="w-10 h-10 mb-2" fill="#9CA3AF" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                      <path d="M12 15.5a3 3 0 100-6 3 3 0 000 6z"/>
                      <path fillRule="evenodd" d="M1.323 11.447C2.811 6.976 7.028 3.75 12.001 3.75c4.97 0 9.185 3.223 10.675 7.69.12.362.12.752 0 1.113-1.487 4.471-5.705 7.697-10.677 7.697-4.97 0-9.186-3.223-10.675-7.69a1.762 1.762 0 010-1.113zM17.25 12a5.25 5.25 0 11-10.5 0 5.25 5.25 0 0110.5 0z" clipRule="evenodd"/>
                    </svg>
                    <p className="text-sm text-gray-500">
                      {t("trips.myTrips.itinerary.customPoiModal.image.uploadText")}
                    </p>
                    <p className="text-xs text-gray-400 mt-1">
                      {t("trips.myTrips.itinerary.customPoiModal.image.fileTypes")}
                    </p>
                  </div>
                  <input
                    type="file"
                    className="hidden"
                    accept="image/jpeg,image/jpg,image/png,image/webp"
                    onChange={handleImageUpload}
                  />
                </label>
              )}
            </div>
          </div>
        </div>

        {/* Sticky Footer */}
        <div className="flex-shrink-0 border-t border-gray-200 px-8 py-4">
          {error && (
            <div className="text-primary-color text-center mb-3">{error}</div>
          )}
          <Button
            className="w-full"
            color="primary"
            text={t("trips.myTrips.itinerary.customPoiModal.submit")}
            disabled={disabled}
            onClick={handleSubmit}
          />
        </div>
      </div>
    </Modal>,
    document.getElementById("modal-root") || document.body
  );
};

export default StoragePoiModal;
